import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { hasVisibleStackedChartValue } from '@/features/executionRuns/pages/executionRunsCharts';
import { getErrorCopy } from '@/shared/copy/errors';
import { s, typographyTokens } from '@/theme/tokens';
import { StatusBanner } from '@/ui';
import { CHART_EMPTY_STATE_NO_DATA_LABEL, ChartEmptyState } from '@/ui/composites';
import { Box, ChartSwatch, Text } from '@/ui/primitives';
import { formatChartTooltipDateLabel } from '@/ui/primitives/chartDateLabel';
import { StackedBarChart } from '@/ui/primitives/StackedBarChart';
import { StackedBarChartSkeleton } from '@/ui/primitives/StackedBarChartSkeleton';
import { chartLoadErrorBannerSx } from './chartLoadErrorBannerSx';
import { ChartPanelTitle } from './ChartPanelTitle';
import { MetricsChartPanelFrame } from './MetricsChartPanelFrame';
import { type StackedMetricsStatusChartPanelLayout } from './StackedMetricsStatusChartSection.layout';
import {
  formatStackedStatusChartLabel,
  renderStatusChartLegendLoadingState,
} from './StackedMetricsStatusChartSection.states';
import type { StatusPieDatum } from './StackedMetricsStatusChartSection.panelTypes';
import type {
  MetricsStatusChartPanelDisplayModel,
  MetricsStatusChartPanelModelToolbarState,
} from './useMetricsStatusChartPanelModel';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';

const skeletonPlotTopAdjustmentPx = 8;
const skeletonPlotBottomAdjustmentPx = 24;

type StatusStackedChartPanelProps<TStatus extends string> = {
  title: string;
  searchAriaLabel: string;
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel: string;
  availableTags: MetricsToolbarTagOption[];
  loading: boolean;
  showPlaceholder?: boolean;
  toolbarState: MetricsStatusChartPanelModelToolbarState<TStatus>;
  statusPanel: StackedMetricsStatusChartPanelLayout;
  statusChartVisualHeight: number;
  legendData: StatusPieDatum[];
  legendLoading: boolean;
  statusEmptyTitle: string;
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  showStatusChartSkeleton: boolean;
  showChartErrorState: boolean;
  suppressAnimation: boolean;
  showAllXAxisLabels?: boolean;
};

export const StatusStackedChartPanel = function StatusStackedChartPanel<TStatus extends string>({
  title,
  loading,
  showPlaceholder = false,
  statusPanel,
  statusChartVisualHeight,
  legendData,
  legendLoading,
  statusEmptyTitle: _statusEmptyTitle,
  statusChartData,
  showStatusChartSkeleton,
  showChartErrorState,
  suppressAnimation,
  showAllXAxisLabels = true,
}: StatusStackedChartPanelProps<TStatus>): ReactElement {
  const chartHostRef = useRef<HTMLDivElement | null>(null);
  const [chartHeightPx, setChartHeightPx] = useState<number>(statusChartVisualHeight);

  const legendNode = useMemo((): ReactElement | null => {
    if (legendLoading) return renderStatusChartLegendLoadingState();
    if (legendData.length === 0) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: s('lg'),
          rowGap: s('xs'),
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {legendData.map((entry, index) => (
          <Box
            key={`${entry.id}-${String(index)}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: s('md'),
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <ChartSwatch color={entry.color} />
            </Box>
            <Text
              role="body"
              color="textSecondary"
              component="span"
              sx={{
                fontSize: typographyTokens.body.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.label.lineHeight,
                whiteSpace: 'nowrap',
              }}
            >
              {entry.name}
            </Text>
          </Box>
        ))}
      </Box>
    );
  }, [legendData, legendLoading]);

  useEffect(() => {
    const element = chartHostRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let rafId = 0;
    const measure = (): void => {
      const bounds = element.getBoundingClientRect();
      const nextHeight = Math.floor(bounds.height);
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
      setChartHeightPx((current) => (Math.abs(current - nextHeight) >= 1 ? nextHeight : current));
    };
    const schedule = (): void => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };
    schedule();
    const observer = new ResizeObserver(() => {
      schedule();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    setChartHeightPx(statusChartVisualHeight);
  }, [statusChartVisualHeight]);

  const header = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        rowGap: s('lg'),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <ChartPanelTitle title={title} component="h2" fontSizePx={17} showInfoIcon={false} />
      </Box>
      {legendNode ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {legendNode}
        </Box>
      ) : null}
    </Box>
  );

  const resolvedChartHeight = Math.max(140, chartHeightPx);
  const hasVisibleStatusChartData =
    statusChartData !== null &&
    hasVisibleStackedChartValue(statusChartData.data, statusChartData.series);
  const showChartSkeleton = loading || showStatusChartSkeleton;

  return (
    <MetricsChartPanelFrame
      height={statusPanel.height}
      header={header}
      headerPaddingX={s('xl')}
      headerPaddingTop={s('lg')}
      headerPaddingBottom={s('xs')}
      contentPaddingBottom={s('md')}
      headerSx={{
        minHeight: `${statusPanel.headerHeight}px`,
      }}
      contentSx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          px: statusPanel.chartHostPaddingX,
          pt: statusPanel.chartHostPaddingTop,
          pb: statusPanel.chartHostPaddingBottom,
          boxSizing: 'border-box',
        }}
      >
        <Box ref={chartHostRef} sx={{ flex: '1 1 auto', minHeight: 0 }}>
          {showPlaceholder ? (
            <Box aria-hidden="true" sx={{ height: resolvedChartHeight }} />
          ) : showChartSkeleton ? (
            <StackedBarChartSkeleton
              height={resolvedChartHeight}
              barCornerRadius={statusPanel.barCornerRadius}
              categoryGapRatio={statusPanel.categoryGapRatio}
              marginLeft={statusPanel.chartMargins.left}
              marginRight={statusPanel.chartMargins.right}
              marginTop={statusPanel.headerToChartGap + skeletonPlotTopAdjustmentPx}
              marginBottom={Math.max(
                0,
                statusPanel.chartMargins.bottom + skeletonPlotBottomAdjustmentPx,
              )}
              yAxisWidth={statusPanel.yAxisWidth}
              hideYAxisLine
            />
          ) : showChartErrorState && !hasVisibleStatusChartData ? (
            <Box
              sx={{
                height: resolvedChartHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StatusBanner
                tone="red"
                variant="outlined"
                message={getErrorCopy('banner.chartLoadFailed')}
                sx={chartLoadErrorBannerSx}
              />
            </Box>
          ) : statusChartData && hasVisibleStatusChartData ? (
            <StackedBarChart
              data={statusChartData.data}
              series={statusChartData.series}
              height={resolvedChartHeight}
              showAllXAxisLabels={showAllXAxisLabels}
              xAxisLabelFormatter={formatStackedStatusChartLabel}
              tooltipLabelFormatter={formatChartTooltipDateLabel}
              marginLeft={statusPanel.chartMargins.left}
              marginRight={statusPanel.chartMargins.right}
              marginTop={statusPanel.headerToChartGap}
              marginBottom={statusPanel.chartMargins.bottom}
              yAxisWidth={statusPanel.yAxisWidth}
              yAxisAutoWidth={statusPanel.yAxisAutoWidth}
              yAxisAutoWidthPadding={statusPanel.yAxisAutoWidthPadding}
              yAxisHeadroomTicks={1}
              yAxisTopPaddingRatio={0}
              axisStrokeWidth={statusPanel.axisStrokeWidth}
              barCornerRadius={statusPanel.barCornerRadius}
              categoryGapRatio={statusPanel.categoryGapRatio}
              barGapRatio={statusPanel.barGapRatio}
              yAxisLabelOffset={statusPanel.yAxisLabelOffset}
              hideXAxisTicks={statusPanel.hideXAxisTicks}
              xAxisLabelOffset={statusPanel.xAxisLabelOffset}
              showHorizontalGrid
              showPlotAreaFrame
              hideXAxisLine={false}
              hideYAxisLine
              skipAnimation={suppressAnimation}
              tooltipEmptyValueLabel="No runs"
            />
          ) : (
            <Box
              sx={{
                height: resolvedChartHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChartEmptyState title={CHART_EMPTY_STATE_NO_DATA_LABEL} variant="metricCard" />
            </Box>
          )}
        </Box>
      </Box>
    </MetricsChartPanelFrame>
  );
};
